import { useTenderDeadline } from "../../../../hooks/useTenderDeadline";
import React, { useEffect, useState } from "react";
import cn from "classnames";
import moment from "moment";
import { toast } from "react-toastify";
import style from "./ListSuggestedPricesNew.module.scss";
import { Order } from "../../../../services";
import { numberToFinanceFormat } from "../../../../utils";
import { TenderDeadlineText } from "../../../shared/TenderDeadlineText/TenderDeadlineText";
import { CURRENCIES } from "../../../../mock";
import CommentIcon from "../../../shared/icons/CommentIcon";
import AppTooltip from "../../../shared/AppTooltip/AppTooltip";
import { useTranslation } from "react-i18next";
import ConfirmActionModal from "../../order-info-expeditor/components/ConfirmActionModal/ConfirmActionModal";
import RebidNotice from "../../order-info/components/RebidNotice/RebidNotice";
import { InfoIcon } from "lucide-react";
import { isRailwayOrder } from "../../../../pages/orders/utils/railwayOrder";

const hasValue = (v) => v !== undefined && v !== null && v !== "";
const EMPTY_VALUE = "-";

const ListSuggestedPricesNew = ({
  items = [],
  orderData = [],
  fetchOrderData,
  ordersStatus,
  onUpdateData,
  isPublic = false,
  user = "",
  tenderDateEnd,
  cargoDetails,
  isMarketSuggested = false,
  isDriverSuggested = false,
  title = "orderInfoClient.suggestedPrices.defaultTitle",
}) => {
  const { isExpired: isTenderExpiredByServer, isReady: isTenderTimeReady } = useTenderDeadline(tenderDateEnd);
  const { t } = useTranslation();
  const isRailway = isRailwayOrder(orderData);

  const [isLoading, setLoading] = useState(false);
  const [activeCommentId, setActiveCommentId] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, id: null, isPublic: null });

  const toggleComment = (priceId) => {
    setActiveCommentId((prevId) => (prevId === priceId ? null : priceId));
  };

  const openConfirmModal = (id, isPublicValue) => {
    setConfirmModal({ isOpen: true, id, isPublic: isPublicValue });
  };

  const handleConfirmAccept = () => {
    const { id } = confirmModal;
    setLoading(true);

    Order.acceptOrderBidById(id, { moderator: user?.name })
      .then((res) => res.data)
      .then(() => {
        toast.success(t("orderInfo.listSuggestedPrices.acceptSuccess"));
        setLoading(false);
        setConfirmModal({ isOpen: false, id: null, isPublic: null });
        onUpdateData?.();
        fetchOrderData?.();
      })
      .catch((error) => {
        console.error("Accept order bid error:", error);
        toast.error(t("orderInfo.listSuggestedPrices.acceptError"));
        setLoading(false);
        setConfirmModal({ isOpen: false, id: null, isPublic: null });
      });
  };

  const handleCancelConfirm = () => {
    setConfirmModal({ isOpen: false, id: null, isPublic: null });
  };

  const canAccept = ordersStatus === "awaitingoffer" || ordersStatus === "published";

  const isTenderEnded = tenderDateEnd && isTenderExpiredByServer;

  return (
    <div className={cn(style.card, { [style.railway_card]: isRailway })}>
      {isMarketSuggested && orderData?.type === 0 && !isDriverSuggested && (
        <span className={style.note}>{t("orderInfoClient.suggestedPrices.marketplaceNote")}</span>
      )}

      <div className={style.topbar}>
        <span className={style.title}>{t(title)}</span>
        {tenderDateEnd && <span className={style.deadline}><TenderDeadlineText date={tenderDateEnd} /></span>}
      </div>

      {orderData?.isInRebid === true && isTenderTimeReady && !isTenderEnded && (
        <RebidNotice>{t("orderInfoClient.rebid.clientNotice")}</RebidNotice>
      )}

      <div className={style.head_row}>
        <span className={style.col_head}>
          {t(`orderInfoClient.suggestedPrices.${isRailway ? "columnCompanyAndDate" : "columnName"}`)}
        </span>
        {!isRailway && <span className={style.col_head}>{t("orderInfoClient.suggestedPrices.columnDate")}</span>}
        <span className={style.col_head}>
          {t(`orderInfoClient.suggestedPrices.${isRailway ? "columnBid" : "columnSuggestedAmount"}`)}
        </span>
        <span className={style.col_head}>
          {t(`orderInfoClient.suggestedPrices.${isRailway ? "columnTerm" : "columnDeliveryTerm"}`)}
        </span>
        {isRailway && <span className={style.col_head}>{t("orderInfoClient.suggestedPrices.columnWagons")}</span>}
        <span className={style.col_head}/>
      </div>

      {items.length === 0 && <div className={style.empty}>{t("orderInfoClient.suggestedPrices.empty")}</div>}

      {items.map((item, index) => {
        const currencySymbol = CURRENCIES[item?.currency || "KZT"]?.symbol || "";
        const hasCommentDetails = item?.comments || hasValue(item?.weight) || hasValue(item?.volume);
        const bidName = item.expeditorName || item?.userName || EMPTY_VALUE;
        const bidNameTooltipId = `suggested-price-name-tooltip-${item.id || index}`;

        return (
          <React.Fragment key={item.id}>
            <div className={style.row}>
              {isRailway ? (
                <div className={style.company_cell}>
                  <span className={cn(style.cell, style.cell_name)} data-tooltip-id={bidNameTooltipId}>
                    {bidName}
                  </span>
                  <span className={cn(style.cell, style.cell_date)}>
                    {item?.updatedAt ? moment(item.updatedAt).format("DD.MM.YYYY, HH:mm") : EMPTY_VALUE}
                  </span>
                </div>
              ) : (
                <>
                  <span className={cn(style.cell, style.cell_name)} data-tooltip-id={bidNameTooltipId}>
                    {bidName}
                  </span>
                  <span className={style.cell}>
                    {moment(item?.updatedAt).format("DD.MM.YYYY")} ({moment(item?.updatedAt).format("HH:mm")})
                  </span>
                </>
              )}
              <span
                className={style.cell_amount}
                data-label={isRailway ? t("orderInfoClient.suggestedPrices.columnBid") : undefined}
              >
                {numberToFinanceFormat(item.price)} {currencySymbol}
              </span>
              <span
                className={cn(style.cell, style.cell_term)}
                data-label={isRailway ? t("orderInfoClient.suggestedPrices.columnTerm") : undefined}
              >
                {item?.plannedDays ? t("orderInfoClient.suggestedPrices.days", { count: item.plannedDays }) : EMPTY_VALUE}
                {!isRailway && hasCommentDetails && (
                  <button className={style.comment_btn} onClick={() => toggleComment(item.id)} title={t("orderInfoClient.suggestedPrices.commentTitle")}>
                    <CommentIcon type={3} fill="#212121" />
                  </button>
                )}
              </span>
              {isRailway && (
                <span
                  className={cn(style.cell, style.cell_wagons)}
                  data-label={t("orderInfoClient.suggestedPrices.columnWagons")}
                >
                  {hasValue(item?.wagonsCount) ? item.wagonsCount : EMPTY_VALUE}
                  {hasCommentDetails && (
                    <button
                      className={style.comment_btn}
                      onClick={() => toggleComment(item.id)}
                      title={t("orderInfoClient.suggestedPrices.commentTitle")}
                    >
                      <CommentIcon type={3} fill="#212121" />
                    </button>
                  )}
                </span>
              )}
              <div className={style.actions}>
                {canAccept ? (
                  <button
                    className={style.accept_btn}
                    onClick={() => openConfirmModal(item.id, isPublic)}
                    disabled={isLoading}
                  >
                    {t("orderInfoClient.suggestedPrices.accept")}
                  </button>
                ) : item?.isAccepted ? (
                  <span className={style.accepted}>{t("orderInfoClient.suggestedPrices.accepted")}</span>
                ) : null}
              </div>
            </div>
            {item?.isExAccepted === true && (
              <div className={style.executor_note}>
                <InfoIcon size={16} color="#22659A" />
                <span>{t("orderInfoClient.suggestedPrices.orderWithdrawnFromPartner")}</span>
              </div>
            )}
            <AppTooltip id={bidNameTooltipId} place="top" positionStrategy="fixed">
              {bidName}
            </AppTooltip>

            {activeCommentId === item.id && hasCommentDetails && (
              <div className={style.comment_box}>
                <div className={style.comment_box_head}>{t("orderInfoClient.suggestedPrices.commentTitle")}</div>
                <div className={style.comment_box_body}>
                  {item?.comments && <span>{item.comments}</span>}
                  {hasValue(item?.weight) && (
                    <span>
                      <b>{t("orderInfoClient.suggestedPrices.weight")}</b> {item.weight}
                    </span>
                  )}
                  {hasValue(item?.volume) && (
                    <span>
                      <b>{t("orderInfoClient.suggestedPrices.volume")}</b> {item.volume}
                    </span>
                  )}
                </div>
              </div>
            )}
          </React.Fragment>
        );
      })}

      <ConfirmActionModal
        isOpen={confirmModal.isOpen}
        title={t("orderInfo.listSuggestedPrices.confirmMessage")}
        onConfirm={handleConfirmAccept}
        onCancel={handleCancelConfirm}
        isLoading={isLoading}
      />
    </div>
  );
};

export default ListSuggestedPricesNew;
